import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * MoneyInput — input numerico con formato italiano (1.234,56) per importi in €.
 *
 * Props:
 *  - value: numero o stringa numerica (es. 1234.56 oppure "1234.56" oppure "")
 *  - onChange: (rawNumeric: string|number) => void
 *      Riceve la rappresentazione "raw" pronta da salvare: stringa con punto come decimale
 *      (es. "1234.56"), oppure "" se vuoto.
 *  - prefix: stringa prima (default "€")
 *  - decimals: cifre decimali ammesse (default 2)
 *  - placeholder, className, "data-testid", disabled, id ecc. inoltrati al sottostante Input
 */
const _it = (n, decimals) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  const fixed = num.toFixed(decimals);
  const negative = fixed.startsWith("-");
  const [intRaw, dec] = (negative ? fixed.slice(1) : fixed).split(".");
  const intWithSep = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const out = decimals > 0 ? `${intWithSep},${dec}` : intWithSep;
  return negative ? `-${out}` : out;
};

const _toRaw = (it) => {
  // Italiano: 1.234,56 → 1234.56  |  "1.234"→"1234"  |  "" → ""
  const s = String(it || "").trim();
  if (!s) return "";
  // Tieni solo cifre, , - e .
  const clean = s.replace(/[^0-9,.\-]/g, "");
  // Sostituisci punti (migliaia) con vuoto, virgola con punto (decimale)
  const noThousands = clean.replace(/\./g, "");
  return noThousands.replace(/,/g, ".");
};

export const MoneyInput = ({
  value,
  onChange,
  prefix = "€",
  decimals = 2,
  placeholder = "0,00",
  className = "",
  inputClassName = "",
  ...rest
}) => {
  // Stato locale per mostrare la versione formattata ed evitare ri-render mentre l'utente scrive
  const [text, setText] = useState(() => (value === "" || value == null) ? "" : _it(value, decimals));
  const [focused, setFocused] = useState(false);

  // Sincronizza dall'esterno (solo quando NON in editing per non bloccare la digitazione)
  useEffect(() => {
    if (focused) return;
    setText((value === "" || value == null) ? "" : _it(value, decimals));
  }, [value, decimals, focused]);

  const handleChange = (e) => {
    const raw = e.target.value;
    // Permetti solo cifre, virgola, punto, segno meno
    const filtered = raw.replace(/[^0-9,.\-]/g, "");
    setText(filtered);
    const numericRaw = _toRaw(filtered);
    onChange?.(numericRaw === "" || numericRaw === "-" ? "" : numericRaw);
  };

  const handleBlur = () => {
    setFocused(false);
    const numericRaw = _toRaw(text);
    if (numericRaw === "" || numericRaw === "-") { setText(""); return; }
    const n = Number(numericRaw);
    if (Number.isFinite(n)) setText(_it(n, decimals));
  };

  return (
    <div className={`relative ${className}`}>
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-500 select-none">{prefix}</span>
      <Input
        {...rest}
        type="text"
        inputMode="decimal"
        value={text}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`pl-7 tabular-nums text-left ${inputClassName} ${rest.className || ""}`}
      />
    </div>
  );
};

export default MoneyInput;
